import React, { useEffect, useState } from 'react';
import { Sparkles, X, KeyRound, Settings as SettingsIcon, ChefHat, Users } from 'lucide-react';
import { useT } from '../context/LanguageContext';

/**
 * Slide-in welcome card shown once after the post-login intro animation
 * completes. Auto-dismisses after AUTO_DISMISS_MS, can be closed manually.
 *
 * Plays a short soft tone of its own — separate from the login chime — so the
 * user gets an audible cue that something new appeared.
 */
const AUTO_DISMISS_MS = 14000;

// Module-level, so the wording is held as translation KEYS and resolved at render.
const TIPS = [
  {
    Icon: ChefHat,
    titleKey: 'dashboard.welcomeTips.menu.title',
    bodyKey: 'dashboard.welcomeTips.menu.body',
  },
  {
    Icon: Users,
    titleKey: 'dashboard.welcomeTips.team.title',
    bodyKey: 'dashboard.welcomeTips.team.body',
  },
  {
    Icon: SettingsIcon,
    titleKey: 'dashboard.welcomeTips.settings.title',
    bodyKey: 'dashboard.welcomeTips.settings.body',
  },
  {
    Icon: KeyRound,
    titleKey: 'dashboard.welcomeTips.roles.title',
    bodyKey: 'dashboard.welcomeTips.roles.body',
  },
];

function playWelcomeTone() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended' && ctx.resume) ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    const note = (freq, start, dur, gain = 0.06) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq, now + start);
      g.gain.setValueAtTime(0, now + start);
      g.gain.linearRampToValueAtTime(gain, now + start + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      o.connect(g).connect(ctx.destination);
      o.start(now + start);
      o.stop(now + start + dur + 0.05);
    };
    // E5 → A5 — soft, neutral arrival tone (gentler than the login chime)
    note(659.25, 0.00, 0.45, 0.05);
    note(880.00, 0.08, 0.60, 0.06);
    setTimeout(() => ctx.close && ctx.close().catch(() => {}), 1200);
  } catch (_) { /* audio unavailable */ }
}

export default function WelcomeTipsToast({ user, onClose }) {
  const [closing, setClosing] = useState(false);
  const { t } = useT();

  useEffect(() => {
    playWelcomeTone();
    const t = setTimeout(() => handleClose(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => onClose && onClose(), 280);
  };

  const firstName = user?.first_name || t('dashboard.welcomeTips.there');

  return (
    <div
      className={`fixed bottom-6 right-6 z-[9000] w-[360px] max-w-[calc(100vw-2rem)] transition-all duration-300 ${
        closing ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0'
      }`}
    >
      <style>{`
        @keyframes wt-slide-in {
          0%   { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .wt-card {
          background: linear-gradient(180deg, #ffffff, #f8fafc);
          border: 1px solid rgba(212, 175, 55, 0.35);
          border-radius: 12px;
          box-shadow:
            0 24px 60px -16px rgba(0,0,0,0.25),
            0 4px 16px -4px rgba(0,0,0,0.08),
            0 0 0 1px rgba(212,175,55,0.08);
          animation: wt-slide-in 0.4s cubic-bezier(.2,.7,.2,1) both;
        }
        .wt-header {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border-bottom: 1px solid rgba(212, 175, 55, 0.30);
        }
      `}</style>

      <div className="wt-card overflow-hidden">
        <div className="wt-header px-4 py-3 flex items-start gap-3">
          <div className="mt-0.5 rounded-full bg-amber-500 p-1.5 text-white shadow-sm">
            <Sparkles size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold tracking-[0.3em] uppercase text-amber-800">
              {t('common.welcome')}
            </div>
            <div className="text-base font-semibold text-slate-900 truncate">
              {t('dashboard.welcomeTips.hi', { name: firstName })}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-amber-900/60 hover:text-amber-900 rounded p-1 -mr-1"
            aria-label={t('dashboard.welcomeTips.dismiss')}
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3">
          <p className="text-sm text-slate-600 mb-3">
            {t('dashboard.welcomeTips.intro')} <span className="font-semibold text-slate-900">{t('common.appName')}</span>.
            {' '}{t('dashboard.welcomeTips.introEnd')}
          </p>
          <ul className="space-y-2.5">
            {TIPS.map(({ Icon, titleKey, bodyKey }) => (
              <li key={titleKey} className="flex items-start gap-2.5">
                <div className="mt-0.5 rounded-md bg-slate-100 text-slate-600 p-1.5 flex-shrink-0">
                  <Icon size={13} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-900">{t(titleKey)}</div>
                  <div className="text-xs text-slate-500 leading-snug">{t(bodyKey)}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 text-right">
          <button
            type="button"
            onClick={handleClose}
            className="text-xs font-semibold text-amber-700 hover:text-amber-900 tracking-wider uppercase"
          >
            {t('dashboard.welcomeTips.gotIt')}
          </button>
        </div>
      </div>
    </div>
  );
}
